import merge from 'lodash/merge';
import Bugsnag from '@bugsnag/js';

import { actions } from 'checkout/state/checkoutActions';
import { IAddress } from 'checkout/types/Address';
import { IBillingInfo, ICreditCard } from 'checkout/types/Billing';
import { IPartialOrder } from 'checkout/types/Order';
import { ITabName } from 'checkout/types/Tab';
import { ICheckoutSections, ICheckoutSectionStatus } from 'checkout/types/Section';
import { UserType } from 'shared/types/User';
import {
  IAction,
  IAddressAction,
  IAddressesAction,
  IBillingInfoAction,
  IBillingInfosAction,
  IConnectAddressSuccessAction,
  ICreditCardSuccessAction,
  IErrorAction,
  IExpressPayEnabledAction,
  IGiftCardSuccessAction,
  IOrderSuccessAction,
  IPartialOrderSuccessAction,
  IPaymentsAvailabilitySuccessAction,
  IProductTemplateSuccessAction,
  ISectionEditAction,
  ISectionLoadingAction,
  ITabChangeAction,
  IUserTypeAction,
  IVerifyCreditCardSuccessAction,
  IWantSuccessAction,
} from 'checkout/types/ActionPayload';
import { IAvailablePaymentMethods } from 'checkout/types/PaymentMethod';
import { PaymentType } from 'shared/enums/PaymentType';
import isEmpty from 'lodash/isEmpty';
import { ISize } from 'shared/types/Size';
import { IPartialProductTemplate } from 'shared/types/ProductTemplate';
import { IGiftCard } from 'checkout/types/GiftCard';

interface IOrderWant {
  boxCondition: string;
  condition: number;
  productTemplate: IPartialProductTemplate;
  shoeCondition: string;
  size: number;
  sizeOption: ISize;
  status: string;
  variantShoeCondition: string;
  offerAmountCents?: number;
  offerDuration?: number;
}

export interface ICheckoutState {
  addresses: { [id: string]: IAddress };
  availablePaymentMethods: IAvailablePaymentMethods;
  applePayAvailable?: boolean;
  billingInfos?: { [id: string]: IBillingInfo };
  creditCards?: ICreditCard[];
  creditCardVerificationLoading: boolean;
  displayInternationalFeeWarning: boolean;
  errorModalMessage?: string;
  giftCard?: IGiftCard;
  isErrorUnrecoverable?: boolean;
  isExpressPayModalVisible?: boolean;
  isGuestCheckout: boolean;
  order?: IPartialOrder;
  loading: {
    availablePaymentMethods: boolean;
    address: boolean;
    billingInfo: boolean;
    giftCard?: boolean;
    order: boolean;
    want?: boolean;
    productTemplate?: boolean;
  };
  orderReadyForPurchase: boolean;
  orderSubmitting: boolean;
  tab: ITabName;
  sections: ICheckoutSectionStatus;
  userType: UserType;
  want?: IOrderWant;
}

export const INITIAL_CHECKOUT_STATE: ICheckoutState = {
  addresses: {},
  availablePaymentMethods: {},
  billingInfos: {},
  creditCardVerificationLoading: false,
  displayInternationalFeeWarning: false,
  giftCard: {},
  isExpressPayModalVisible: true,
  isGuestCheckout: false,
  loading: {
    availablePaymentMethods: false,
    address: true,
    billingInfo: false,
    order: true,
  },
  orderReadyForPurchase: false,
  orderSubmitting: false,
  tab: ITabName.SHIPPING,
  sections: {
    shippingAddress: {
      complete: false,
      editing: false,
      loading: false,
    },
    shippingMethod: {
      complete: false,
      editing: false,
      loading: false,
    },
    payment: {
      complete: false,
      editing: false,
      loading: false,
    },
  },
  userType: 'guest',
};

/**
 * **************************************************
 * Action Type Guards
 *
 * Type guards allow us to do two things:
 * 1) Sort actions into useful groups that share state mutation
 * 2) Associate interfaces to actions
 *
 * You can read more about type guards in the typescript docs:
 * https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards
 * **************************************************
 */

/**
 * *************************
 * Address Type Guards
 * *************************
 */
export const isAddressLoading = (action: IAction) =>
  action.type === actions.fetchAddressRequest || action.type === actions.fetchAddressesRequest;

export const isAddressFailure = (action: IAction) =>
  action.type === actions.fetchAddressFailure || action.type === actions.fetchAddressesFailure;

export const isAddressSuccess = (action: IAction): action is IAddressAction | IAddressesAction =>
  action.type === actions.createAddressSuccess ||
  action.type === actions.deleteAddressSuccess ||
  action.type === actions.fetchAddressSuccess ||
  action.type === actions.fetchAddressesSuccess;

export const isConnectAddressSuccess = (action: IAction): action is IConnectAddressSuccessAction =>
  action.type === actions.connectAddressSuccess;

/**
 * *************************
 * Billing Info Type Guards
 * *************************
 */
export const isBillingInfoLoading = (action: IAction) =>
  action.type === actions.fetchBillingInfosRequest ||
  action.type === actions.fetchBillingInfoRequest;

export const isBillingInfoFailure = (action: IAction): action is IErrorAction =>
  action.type === actions.createBillingInfoFailure ||
  action.type === actions.fetchBillingInfosFailure ||
  action.type === actions.fetchBillingInfoFailure;

export const isBillingInfoSuccess = (
  action: IAction,
): action is IBillingInfoAction | IBillingInfosAction =>
  action.type === actions.createBillingInfoSuccess ||
  action.type === actions.fetchBillingInfosSuccess ||
  action.type === actions.fetchBillingInfoSuccess;

/**
 * *************************
 * Error Type Guards
 * *************************
 */
export const isCheckoutFailure = (action: IAction): action is IErrorAction =>
  action.type === actions.checkoutFailure;

export const isUnrecoverableFailure = (action: IAction): action is IErrorAction =>
  action.type === actions.sessionTimeoutFailure;

/**
 * *************************
 * Gift Card Type Guards
 * *************************
 */
export const isGiftCardRequestLoading = (action: IAction) =>
  action.type === actions.fetchGiftCardRequest;

export const isGiftCardRequestSuccess = (action: IAction): action is IGiftCardSuccessAction =>
  action.type === actions.fetchGiftCardSuccess;

export const isGiftCardRequestFailure = (action: IAction): action is IErrorAction =>
  action.type === actions.fetchGiftCardFailure;

/**
 * *************************
 * Order Type Guards
 * *************************
 */
export const isOrderLoading = (action: IAction) =>
  action.type === actions.expressCheckoutRequest ||
  action.type === actions.fetchOrderRequest ||
  action.type === actions.createGuestOrderRequest;

export const isPurchaseOrderRequest = (action: IAction) =>
  action.type === actions.purchaseOrderRequest;

export const isOrderRequestSuccess = (action: IAction): action is IOrderSuccessAction =>
  action.type === actions.connectAddressSuccess ||
  action.type === actions.createGuestOrderSuccess ||
  action.type === actions.fetchOrderSuccess ||
  action.type === actions.fetchPaymentMethodSuccess ||
  action.type === actions.fetchPromotionSuccess ||
  action.type === actions.updateOrderSuccess ||
  action.type === actions.updateShippingServiceLevelSuccess;

export const isPartialOrderSuccess = (action: IAction): action is IPartialOrderSuccessAction =>
  action.type === actions.orderFromQueryParamsSuccess ||
  action.type === actions.fetchPriceEstimateSuccess ||
  action.type === actions.fetchGuestPriceEstimateSuccess ||
  action.type === actions.fetchGuestPriceBreakdownSuccess;

export const isOrderRequestFailure = (action: IAction): action is IErrorAction =>
  action.type === actions.createGuestOrderFailure ||
  action.type === actions.fetchOrderFailure ||
  action.type === actions.fetchGuestPriceEstimateFailure ||
  action.type === actions.fetchGuestPriceBreakdownFailure ||
  action.type === actions.updateOrderFailure;

export const isPurchaseOrderFailure = (action: IAction): action is IErrorAction =>
  action.type === actions.expressCheckoutFailure || action.type === actions.purchaseOrderFailure;

/**
 * *************************
 * Section Type Guards
 * *************************
 */
export const isSectionEditing = (action: IAction): action is ISectionEditAction =>
  action.type === actions.sectionEdit;

export const isSectionError = (action: IAction): action is IErrorAction =>
  action.type === actions.createAddressFailure ||
  action.type === actions.createKlarnaSessionFailure ||
  action.type === actions.createPayPalSessionFailure ||
  action.type === actions.deleteAddressFailure ||
  action.type === actions.displayGlobalError ||
  action.type === actions.fetchAddressFailure ||
  action.type === actions.fetchAddressesFailure ||
  action.type === actions.fetchPaymentMethodFailure ||
  action.type === actions.fetchPromotionFailure ||
  action.type === actions.updateShippingServiceLevelFailure;

export const isSectionLoading = (action: IAction): action is ISectionLoadingAction =>
  action.type === actions.createAddressRequest ||
  action.type === actions.createKlarnaSessionRequest ||
  action.type === actions.createPayPalSessionRequest ||
  action.type === actions.deleteAddressRequest ||
  action.type === actions.fetchAddressRequest ||
  action.type === actions.fetchAddressesRequest ||
  action.type === actions.fetchBillingInfoRequest ||
  action.type === actions.fetchBillingInfosRequest ||
  action.type === actions.fetchPaymentMethodRequest ||
  action.type === actions.promotionLoading ||
  action.type === actions.updateShippingServiceLevelRequest;

/**
 * *************************
 * Payment Type Guards
 * *************************
 */
export const isExpressPayAvailable = (action: IAction): action is IExpressPayEnabledAction =>
  action.type === actions.applePayAvailable;

export const isCreditCardRequestSuccess = (action: IAction): action is ICreditCardSuccessAction =>
  action.type === actions.fetchCreditCardSuccess;

export const isVerifyCreditCardSuccess = (
  action: IAction,
): action is IVerifyCreditCardSuccessAction => action.type === actions.verifyCreditCardSuccess;

export const isVerifyCreditCardFailure = (action: IAction): action is IErrorAction =>
  action.type === actions.verifyCreditCardFailure;

/**
 * *************************
 * Payment Type Guards
 * *************************
 */
export const isProductTemplateRequestLoading = (action: IAction) =>
  action.type === actions.fetchProductTemplateRequest;

export const isProductTemplateRequestSuccess = (
  action: IAction,
): action is IProductTemplateSuccessAction => action.type === actions.fetchProductTemplateSuccess;

export const isProductTemplateRequestFailure = (action: IAction): action is IErrorAction =>
  action.type === actions.fetchProductTemplateFailure;

export const isPaymentsAvailabilityRequestLoading = (action: IAction) =>
  action.type === actions.fetchAvailablePaymentMethodsRequest;

export const isPaymentsAvailabilityRequestSuccess = (
  action: IAction,
): action is IPaymentsAvailabilitySuccessAction =>
  action.type === actions.fetchAvailablePaymentMethodsSuccess;

export const isPaymentsAvailabilityRequestFailure = (action: IAction): action is IErrorAction =>
  action.type === actions.fetchAvailablePaymentMethodsFailure;

/**
 * *************************
 * Tab Type Guards
 * *************************
 */
export const isChangingTab = (action: IAction): action is ITabChangeAction =>
  action.type === actions.changeTab;

/**
 * *************************
 * User Type Guards
 * *************************
 */
export const isContinuingAsGuest = (action: IAction) => action.type === actions.continueAsGuest;

export const isUserTypeUpdate = (action: IAction): action is IUserTypeAction =>
  action.type === actions.userTypeUpdate;

/**
 * *************************
 * Want Type Guards
 * *************************
 */
export const isWantRequestLoading = (action: IAction) => action.type === actions.fetchWantRequest;

export const isWantRequestSuccess = (action: IAction): action is IWantSuccessAction =>
  action.type === actions.fetchWantSuccess;

export const isWantRequestFailure = (action: IAction): action is IErrorAction =>
  action.type === actions.fetchWantFailure;

/**
 * *************************
 * Reducer
 * *************************
 */
export const checkoutReducer = (state: ICheckoutState, action: IAction) => {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.info(`checkout state action: ${action.type}`, (action as IErrorAction)?.payload);
  }

  /**
   * *************************
   * Tab changing Action
   *
   * These actions update which tab the user will see
   * *************************
   */
  if (isChangingTab(action)) {
    state.tab = action.payload;
  }

  /**
   * *************************
   * Section Loading Action
   *
   * These actions target a checkout section using sectionByActionType and set
   * that section to a loading state. The actions are typically fired before async
   * events like an API call then views in checkout section use the <Loader />
   * loading component and display specialized text.
   * *************************
   */
  if (isSectionLoading(action)) {
    const sectionByActionType = new Map<string, ICheckoutSections>([
      [actions.createAddressRequest, 'shippingAddress'],
      [actions.createKlarnaSessionRequest, 'payment'],
      [actions.createPayPalSessionRequest, 'payment'],
      [actions.deleteAddressRequest, 'shippingAddress'],
      [actions.fetchAddressesRequest, 'shippingAddress'],
      [actions.fetchAddressRequest, 'shippingAddress'],
      [actions.fetchBillingInfoRequest, 'payment'],
      [actions.fetchBillingInfosRequest, 'payment'],
      [actions.fetchPaymentMethodRequest, 'payment'],
      [actions.updateShippingServiceLevelRequest, 'shippingMethod'],
    ]);

    const section = sectionByActionType.get(action.type);

    if (section) {
      state.sections[section].complete = false;
      state.sections[section].error = undefined;
      state.sections[section].loading = typeof action.payload === 'boolean' ? action.payload : true;
    }
  }

  /**
   * *************************
   * Section Edit Action
   *
   * This action is triggered by checkout section when the section edit button is
   * clicked. We trigger an action to allow different behavior in certain situations.
   * *************************
   */
  if (isSectionEditing(action)) {
    const section = state.sections[action.payload];

    section.editing = true;
  }

  /**
   * *************************
   * Section Error Action
   *
   * These actions target a checkout section using sectionByActionType and set
   * that section to an error state. Often error actions are fired when API calls
   * fail and need to display feedback to the user.
   * *************************
   */
  if (isSectionError(action)) {
    const sectionByActionType = new Map<string, ICheckoutSections>([
      [actions.createAddressFailure, 'shippingAddress'],
      [actions.createKlarnaSessionFailure, 'payment'],
      [actions.createPayPalSessionFailure, 'payment'],
      [actions.deleteAddressFailure, 'shippingAddress'],
      [actions.fetchAddressFailure, 'shippingAddress'],
      [actions.fetchAddressesFailure, 'shippingAddress'],
      [actions.fetchPaymentMethodFailure, 'payment'],
      [actions.updateShippingServiceLevelFailure, 'shippingMethod'],
    ]);

    const section = sectionByActionType.get(action.type);

    state.errorModalMessage = action.payload;

    if (section) {
      state.loading.order = false;
      state.sections[section].complete = false;
      state.sections[section].editing = true;
      state.sections[section].error = action.payload;
      state.sections[section].loading = false;
    }
  }

  /**
   * *************************
   * Unrecoverable Error Action
   *
   * When a user gets into a state in checkout that cannot be corrected by the user,
   * i.e. product sold out or something happened, we need to force the user out of checkout.
   * *************************
   */
  if (isUnrecoverableFailure(action)) {
    state.errorModalMessage = action.payload;
    state.isErrorUnrecoverable = true;
  }

  /**
   * *************************
   * Order Loading Action
   *
   * These actions trigger a global loading indicator on the checkout page.
   * *************************
   */
  if (isOrderLoading(action)) {
    state.loading.order = true;
  }

  /**
   * *************************
   * Order Success Action
   *
   * All actions that return full order objects are routed to this block. The
   * reducer can determine correct page state for all tabs from that order object.
   * *************************
   */
  if (isOrderRequestSuccess(action)) {
    /**
     * Begin by removing global loading indicator and providing the order object
     * downstream.
     */
    state.loading.order = false;

    // All payloads in this block should be a FULL order object.
    state.order = action.payload;

    /**
     * NOTE: for gift card purchases set default tab to payment
     */
    if (state.order?.product?.giftCardId) {
      state.tab = ITabName.PAYMENT;
    }

    /**
     * shipping address
     */
    const hasShippingAddress = !!state.order.addressId;

    // NOTE: when user is purchasing a gift card no shipping address is required
    state.sections.shippingAddress = {
      complete: state.order?.product?.giftCardId ? true : hasShippingAddress,
      editing: state.order?.product?.giftCardId ? false : !hasShippingAddress,
      loading: false,
    };

    /**
     * shipping method
     * If user has multiple shipping methods will default to the last previous
     * used shipping method. If the user wants to select a new shipping method
     * they can edit
     */
    const hasShippingServiceLevel = !!state.order.shippingServiceLevel;

    const shippingServiceLevelComplete = hasShippingAddress && hasShippingServiceLevel;

    // NOTE: when user is purchasing a gift card no shipping method is required
    state.sections.shippingMethod = {
      complete: state.order?.product?.giftCardId ? true : shippingServiceLevelComplete,
      editing: state.order?.product?.giftCardId ? false : !shippingServiceLevelComplete,
      loading: false,
    };

    /**
     * payment method
     */
    const hasPaymentMethod = !!state.order.billingInfoId;

    /**
     * NOTE: when user is purchasing a gift card we do not accept shipping information
     * so will mark as true to allow them to pass through checkout.
     */
    let paymentMethodComplete =
      (state.order?.product?.giftCardId ? true : shippingServiceLevelComplete) && hasPaymentMethod;

    if (state.order?.paymentType === PaymentType.PayPal) {
      const billingInfo = state.billingInfos[state.order?.billingInfoId];

      paymentMethodComplete = paymentMethodComplete && !isEmpty(billingInfo);
    }

    // Do not allow international shipping addresses to use affirm or afterpay
    if (
      state.addresses[state.order?.addressId]?.countryCode !== 'US' &&
      (state.order?.paymentType === PaymentType.Affirm ||
        state.order?.paymentType === PaymentType.AfterPay)
    ) {
      paymentMethodComplete = false;
    }

    state.sections.payment = {
      complete: paymentMethodComplete,
      editing: !paymentMethodComplete,
      loading: false,
    };
  }

  /**
   * *************************
   * Partial Order Action
   *
   * These actions provide order information to downstream components but do not
   * attempt to update tab state.
   * *************************
   */
  if (isPartialOrderSuccess(action)) {
    state.loading.order = false;

    state.order = merge(state.order, action.payload);
  }

  /**
   * *************************
   * Order Error Action / Checkout Failure Action
   *
   * state.errorModalMessage displays a page-wide error modal that forces the
   * user to exit checkout and try again. Checkout failures happen when the
   * user auth session expires or other errors where the user no longer is
   * allowed in checkout.
   * *************************
   */
  if (isOrderRequestFailure(action) || isCheckoutFailure(action)) {
    state.loading.order = false;
    state.errorModalMessage = action.payload;
  }

  if (action.type === actions.dismissGlobalError) {
    state.errorModalMessage = null;
  }

  /**
   * *************************
   * Purchase Order Request Action
   *
   * This single action is fired right after the user attempts to purchase the
   * products in checkout. The purchase order button that fires this will only
   * be enabled if state.orderReadyForPurchase is true.
   * *************************
   */
  if (isPurchaseOrderRequest(action)) {
    state.orderSubmitting = true;
  }

  /**
   * *************************
   * Purchase Order Failure Action
   *
   * Fired when purchasing an order fails. The payload contains the error message
   * from sneakers.
   * *************************
   */
  if (isPurchaseOrderFailure(action)) {
    state.loading.order = false;
    state.orderSubmitting = false;

    state.errorModalMessage = action.payload;

    const error = new Error(action.payload);

    Bugsnag.notify(error, (event) => {
      event.severity = 'error';
      event.context = 'checkout';
    });
  }

  /**
   * *************************
   * Address Loading Action
   *
   * These actions can trigger a global loading indicators as well as tab specific to address
   * *************************
   */
  if (isAddressLoading(action)) {
    state.loading.address = true;
  }

  if (isAddressFailure(action)) {
    state.loading.address = false;
    // CHECKOUT TODO:
    // update global error state
    // notify bugsnag
  }

  /**
   * *************************
   * Address Success Action
   *
   * Fired when an address is fetched or created
   * *************************
   */
  if (isAddressSuccess(action)) {
    state.loading.address = false;

    switch (action.type) {
      case actions.fetchAddressesSuccess:
        for (const address of action.payload as IAddress[]) {
          state.addresses[address.id] = address;
        }
        break;
      case actions.deleteAddressSuccess: {
        const { id } = action.payload as IAddress;
        delete state.addresses[id];
        state.loading.address = false;
        break;
      }
      default: {
        const { id } = action.payload as IAddress;
        state.addresses[id] = action.payload as IAddress;
        state.loading.address = false;
        break;
      }
    }
  }

  /**
   * *************************
   * Connect Address Actions
   *
   * Fired when the user switches their address
   * *************************
   */
  if (action.type === actions.connectAddressRequest) {
    state.loading.address = true;
  }

  if (action.type === actions.connectAddressFailure) {
    state.loading.address = false;
  }

  if (isConnectAddressSuccess(action)) {
    state.displayInternationalFeeWarning = action.payload.displayInternationalFeeWarning;

    state.loading.address = false;
  }

  /**
   * *************************
   * Shipping Method Actions
   *
   * Fired when the user switches their address
   * *************************
   */
  if (action.type === actions.updateShippingServiceLevelRequest) {
    state.loading.address = true;
  }

  if (
    action.type === actions.updateShippingServiceLevelSuccess ||
    action.type === actions.updateShippingServiceLevelFailure
  ) {
    state.loading.address = false;
  }

  if (action.type === actions.dismissInternationalFeeWarning) {
    state.displayInternationalFeeWarning = false;
  }

  /**
   * *************************
   * Billing Info Loading Action
   *
   * These actions can trigger a global loading indicators as well as tab
   * specific to billing infos
   * *************************
   */
  if (isBillingInfoLoading(action)) {
    state.loading.billingInfo = true;
  }

  if (isBillingInfoFailure(action)) {
    state.loading.billingInfo = false;
    state.errorModalMessage = action.payload;

    const error = new Error(action.payload);

    Bugsnag.notify(error, (event) => {
      event.severity = 'error';
      event.context = 'checkout';
    });
  }

  /**
   * *************************
   * Billing Info Success Action
   *
   * Fired when an billing info is fetched or created
   * *************************
   */
  if (isBillingInfoSuccess(action)) {
    state.loading.billingInfo = false;

    switch (action.type) {
      case actions.fetchBillingInfosSuccess:
        for (const billingInfo of action.payload as IBillingInfo[]) {
          state.billingInfos[billingInfo.id] = billingInfo;
        }
        break;
      default: {
        const { id } = action.payload as IBillingInfo;

        state.billingInfos[id] = action.payload as IBillingInfo;
        break;
      }
    }
  }

  /**
   * *************************
   * Express Pay Available Action
   *
   * useExpressPay fires this event when users can express pay the action
   * shows/hides the relevant UI
   * Express payments include apple pay, google pay, browser saved cc however
   * at this time only apple pay will be utilized
   * *************************
   */
  if (isExpressPayAvailable(action)) {
    const available = action.payload;

    state.applePayAvailable = available;
  }

  if (action.type === actions.dismissExpressPayModal) {
    state.isExpressPayModalVisible = false;
  }

  /**
   * *************************
   * Credit Card Success Action
   * *************************
   */
  if (isCreditCardRequestSuccess(action)) {
    state.creditCards = action.payload;
  }

  /**
   * *************************
   * Verify Credit Card Action
   *
   * When the order object has verificationNeeded the user will need to enter
   * their CVV to use that paymentMethod. onSuccess this event updates the review tab
   * and order object.
   * *************************
   */
  if (action.type === actions.verifyCreditCardRequest) {
    state.creditCardVerificationLoading = true;
  }

  if (isVerifyCreditCardSuccess(action)) {
    state.order.verificationNeeded = false;
    state.creditCardVerificationLoading = false;
  }

  if (isVerifyCreditCardFailure(action)) {
    state.order.verificationNeeded = true;
    state.creditCardVerificationLoading = false;
    state.errorModalMessage = action.payload;
  }

  /**
   * *************************
   * Continue As Guest Action
   *
   * This guest checkout specific action redirects the user into checkout and allows guest
   * users to begin entering their shipping address. In the guest checkout flow a
   * full order is created only after the guest user successfully enters their
   * shipping address.
   * *************************
   */
  if (isContinuingAsGuest(action)) {
    state.isGuestCheckout = true;
    state.loading.address = false;

    state.sections.shippingAddress = {
      complete: false,
      editing: true,
      loading: false,
    };
  }

  /**
   * *************************
   * User Type Update Action
   *
   * Some actions depend on knowing the current userType. This action is fired
   * when a user logs in / out and stores the userType in state.
   * *************************
   */
  if (isUserTypeUpdate(action)) {
    state.userType = action.payload;
  }

  /**
   * *************************
   * Product Template Loading Action
   *
   * Action triggers a global loading indicator on the checkout page.
   * *************************
   */
  if (isProductTemplateRequestLoading(action)) {
    state.loading.productTemplate = true;
  }

  /**
   * *************************
   * Product Template Error Action
   *
   * state.errorModalMessage displays a page-wide error modal that forces the
   * user to exit checkout and try again.
   * *************************
   */
  if (isProductTemplateRequestFailure(action)) {
    state.loading.productTemplate = false;
    state.errorModalMessage = action.payload;
  }

  /**
   * *************************
   * Want Loading Action
   *
   * Action triggers a global loading indicator on the checkout page.
   * *************************
   */
  if (isWantRequestLoading(action)) {
    state.loading.want = true;
  }

  /**
   * *************************
   * Product Template Success Action
   *
   * For guest checkout the order object does not yet exist therefore must combine
   * data from product template response into the appropriate key values within the
   * existing empty order object to populate the missing product and product template
   * data. As of now this data is used primarily for order summary and once the user
   * creates an order then will replace this data
   * *************************
   */
  if (isProductTemplateRequestSuccess(action)) {
    const { productTemplate, queryParams } = action.payload;

    state.loading.productTemplate = false;

    const sizeOption = productTemplate?.sizeOptions.find(
      (option) => option.value === Number(queryParams.size),
    );

    const product = {
      boxCondition: queryParams?.boxCondition,
      mainPictureUrl: productTemplate?.mainPictureUrl,
      productTemplate,
      shoeCondition: queryParams?.shoeCondition,
      size: sizeOption?.value,
      sizeOption,
      status: productTemplate?.status,
      variantShoeCondition: queryParams?.shoeCondition,
    };

    state.order = {
      ...state.order,
      product,
    };
  }

  /**
   * *************************
   * Available Payment Method Actions
   *
   * These actions are fired while fetching the available payment methods
   * *************************
   */
  if (isPaymentsAvailabilityRequestLoading(action)) {
    state.loading.availablePaymentMethods = true;
  }
  if (isPaymentsAvailabilityRequestFailure(action)) {
    state.loading.availablePaymentMethods = false;
    state.availablePaymentMethods = {};
  }
  if (isPaymentsAvailabilityRequestSuccess(action)) {
    state.loading.availablePaymentMethods = false;
    state.availablePaymentMethods = action.payload;
  }

  /**
   * *************************
   * Want Error Action
   *
   * state.errorModalMessage displays a page-wide error modal that forces the
   * user to exit checkout and try again.
   * *************************
   */
  if (isWantRequestFailure(action)) {
    state.loading.want = false;
    state.errorModalMessage = action.payload;
  }

  /**
   * *************************
   * Want Success Action
   *
   * For offers the order object is different than the normal flow in that the
   * product and product template data does not exist. We must combine data from the
   * wants response into the appropriate key values within the existing state order
   * to populate the missing product and product template data.
   * *************************
   */
  if (isWantRequestSuccess(action)) {
    // if payload.product exists, then is a variant (used, custom, etc)
    const want = action.payload?.product || action.payload;

    state.loading.want = false;

    const sizeOption = want?.productTemplate?.sizeOptions.find(
      (option) => option.value === want?.size,
    );

    const product = {
      boxCondition: want?.boxCondition,
      condition: want?.condition,
      productTemplate: want?.productTemplate,
      shoeCondition: want?.shoeCondition,
      size: want?.size,
      sizeOption,
      status: want?.status,
      variantShoeCondition: want?.shoeCondition,
      offerAmountCents: action.payload?.offerAmountCents,
      offerDuration: action.payload?.offerDuration,
    };

    state.want = product;
    state.order = {
      ...state.order,
      product,
    };
  }

  /**
   * *************************
   * Gift Card Loading Action
   *
   * Action triggers a global loading indicator on the checkout page.
   * *************************
   */
  if (isGiftCardRequestLoading(action)) {
    state.loading.giftCard = true;
  }

  /**
   * *************************
   * Gift Card Error Action
   *
   * state.errorModalMessage displays a page-wide error modal that forces the
   * user to exit checkout and try again.
   * *************************
   */
  if (isGiftCardRequestFailure(action)) {
    state.loading.giftCard = false;
    state.errorModalMessage = action.payload;
  }

  /**
   * *************************
   * Gift Card Success Action
   * *************************
   */
  if (isGiftCardRequestSuccess(action)) {
    state.loading.giftCard = false;
    state.giftCard = action.payload;
  }

  /**
   * *************************************************************************************
   *                     _       ______         _____                _
   *                    | |     |  ____|       |  __ \              | |
   *  _ __ ___  __ _  __| |_   _| |__ ___  _ __| |__) |   _ _ __ ___| |__   __ _ ___  ___
   * | '__/ _ \/ _` |/ _` | | | |  __/ _ \| '__|  ___/ | | | '__/ __| '_ \ / _` / __|/ _ \
   * | | |  __/ (_| | (_| | |_| | | | (_) | |  | |   | |_| | | | (__| | | | (_| \__ \  __/
   * |_|  \___|\__,_|\__,_|\__, |_|  \___/|_|  |_|    \__,_|_|  \___|_| |_|\__,_|___/\___|
   *                        __/ |
   *                       |___/
   * *************************************************************************************
   */

  /**
   * *************************
   * state.orderReadyForPurchase
   *
   * Determines if the user can place an order.
   * The immutable logic for it runs on EVERY action.
   * This should always be the last item in the reducer so the state mutations
   * above are reflected.
   * *************************
   */
  const activeSectionsNotEditing = Object.keys(state.sections).every(
    (key) => !state.sections[key as ICheckoutSections].editing,
  );

  // NOTE: when user is purchasing a gift card no shipping address is required
  state.orderReadyForPurchase =
    !!state.order?.billingInfoId &&
    Boolean(state.order?.product?.giftCardId || state.order?.addressId) &&
    !!state.order?.shippingServiceLevel &&
    !state.order?.verificationNeeded &&
    activeSectionsNotEditing;

  return state;
};
